import {
  API_PATHS,
  type FileSessionRequest,
  type SessionJoinResponse,
  type ExportLinkRequest,
  type ExportLinkResponse,
  type SharedSceneResponse,
  type UserSearchResult,
  type FeedbackRequest,
  type FeedbackResponse,
  type DrawingDeleteResponse,
  type DrawingCopyAbandonResponse,
  type DrawingListResponse,
  type DrawingGalleryDetailsResponse,
  type DrawingLoadResponse,
  type DrawingRenameRequest,
  type DrawingSaveResponse,
  type DrawingWriteRequest,
  type DrawingAccessRole,
  type DrawingShareCreateResponse,
  type DrawingShareReadResponse,
  type DrawingShareLookupResponse,
  type DrawingShareRedeemResponse,
  type DrawingReferenceResponse,
  type AiGrantListResponse,
  type AiGrantLookupResponse,
  type MintAiGrantResponse,
  type ImpactDashboardDays,
  type ImpactDashboardResponse,
  type ClientTelemetryEvent,
  type AppActivityInterval,
  API_BASE,
} from "@excalidraw-ms/shared";
import { BACKEND_API_BASE } from "../env";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function url(path: string): string {
  return `${BACKEND_API_BASE}${path}`;
}

function authHeaders(token: string): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
  };
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new BackendError(res.status, res.statusText, body, res.headers.get("x-request-id"));
  }
  return res.json() as Promise<T>;
}

export class BackendError extends Error {
  public readonly backendMessage: string | null;
  public readonly code?: string;
  public readonly requestId?: string;

  constructor(
    public status: number,
    public statusText: string,
    public body: string,
    responseRequestId?: string | null,
  ) {
    super(`Backend HTTP ${status}`);
    this.name = "BackendError";
    this.backendMessage = parseBackendErrorMessage(body);
    const validRequestId = (value: unknown): value is string =>
      typeof value === "string" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
    if (validRequestId(responseRequestId)) this.requestId = responseRequestId;
    try {
      const parsed: unknown = JSON.parse(body);
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "code" in parsed &&
        typeof parsed.code === "string"
      )
        this.code = parsed.code;
      if (
        !this.requestId &&
        typeof parsed === "object" &&
        parsed !== null &&
        "requestId" in parsed &&
        validRequestId(parsed.requestId)
      )
        this.requestId = parsed.requestId;
    } catch {
      // Non-JSON HTTP failures have no structured coordination code.
    }
    if (this.requestId) this.message += ` (requestId: ${this.requestId})`;
  }
}

function parseBackendErrorMessage(body: string): string | null {
  try {
    const parsed = JSON.parse(body) as { error?: unknown };
    return typeof parsed.error === "string" ? parsed.error : null;
  } catch {
    return null;
  }
}

// Re-export the shared type for backward compatibility
export type { ExcalidrawSceneData } from "@excalidraw-ms/shared";

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

export async function getImpactDashboard(
  token: string,
  days: ImpactDashboardDays,
  signal?: AbortSignal,
): Promise<ImpactDashboardResponse> {
  const res = await fetch(url(`${API_PATHS.analyticsImpact}?days=${days}`), {
    method: "GET",
    headers: authHeaders(token),
    signal,
  });
  return handleResponse(res);
}

export async function postClientTelemetry(
  token: string,
  events: ClientTelemetryEvent[],
): Promise<void> {
  const res = await fetch(url(API_PATHS.telemetryEvents), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ events }),
    keepalive: true,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new BackendError(res.status, res.statusText, body, res.headers.get("x-request-id"));
  }
}

export async function postAppActivity(
  token: string,
  intervals: AppActivityInterval[],
): Promise<void> {
  const res = await fetch(url(API_PATHS.telemetryActivity), {
    method: "POST",
    headers: { ...authHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ intervals }),
    keepalive: true,
  });
  if (!res.ok)
    throw new BackendError(res.status, res.statusText, "", res.headers.get("x-request-id"));
}

export async function createDrawing(
  token: string,
  request: DrawingWriteRequest,
  operationId = crypto.randomUUID(),
): Promise<DrawingSaveResponse> {
  const res = await fetch(url(API_PATHS.drawingsCreate), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
      "Idempotency-Key": operationId,
    },
    body: JSON.stringify(request),
  });
  return handleResponse(res);
}

export async function loadDrawing(token: string, drawingId: string): Promise<DrawingLoadResponse> {
  const res = await fetch(url(API_PATHS.drawing(drawingId)), {
    method: "GET",
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function duplicateDrawing(
  token: string,
  drawingId: string,
  operationId: string,
): Promise<DrawingSaveResponse> {
  const res = await fetch(url(API_PATHS.drawingDuplicate(drawingId)), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Idempotency-Key": operationId,
    },
  });
  return handleResponse(res);
}

export async function getDrawingReference(
  token: string,
  drawingId: string,
): Promise<DrawingReferenceResponse> {
  const res = await fetch(url(API_PATHS.drawingReference(drawingId)), {
    method: "GET",
    headers: authHeaders(token),
    cache: "no-store",
  });
  return handleResponse(res);
}

export async function openDrawingReference(
  token: string,
  reference: string,
): Promise<DrawingShareRedeemResponse> {
  const res = await fetch(url(API_PATHS.drawingReferenceOpen), {
    method: "POST",
    headers: { ...authHeaders(token), "Content-Type": "application/json" },
    body: JSON.stringify({ reference }),
  });
  return handleResponse(res);
}

export async function lookupDrawingShare(
  token: string,
  drawingId: string,
  role: "editor" | "viewer",
): Promise<DrawingShareLookupResponse> {
  const res = await fetch(`${url(API_PATHS.drawingShare(drawingId))}?role=${role}`, {
    method: "GET",
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function saveDrawing(
  token: string,
  drawingId: string,
  request: DrawingWriteRequest,
  eTag: string,
  operationId = crypto.randomUUID(),
  collaborationClientId?: string,
): Promise<DrawingSaveResponse> {
  const res = await fetch(url(API_PATHS.drawing(drawingId)), {
    method: "PUT",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
      "If-Match": eTag,
      "Idempotency-Key": operationId,
      ...(collaborationClientId ? { "X-Collab-Client-Id": collaborationClientId } : {}),
    },
    body: JSON.stringify(request),
  });
  return handleResponse(res);
}

/** Sets only the drawing's SPE title; the scene, thumbnail and room are untouched. */
export async function renameDrawing(
  token: string,
  drawingId: string,
  name: string,
): Promise<DrawingSaveResponse> {
  const request: DrawingRenameRequest = { name };
  const res = await fetch(url(API_PATHS.drawing(drawingId)), {
    method: "PATCH",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(request),
  });
  return handleResponse(res);
}

export async function getDrawingGalleryDetails(
  token: string,
  drawingIds: string[],
): Promise<DrawingGalleryDetailsResponse> {
  const res = await fetch(url(API_PATHS.drawingGalleryDetails), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ drawingIds }),
  });
  return handleResponse(res);
}

export async function getDrawingPreview(
  token: string,
  drawingId: string,
  version?: string,
): Promise<Blob> {
  // `v` is never read by the server. It exists so the browser HTTP cache keys
  // on the drawing's current version, which lets an unchanged preview be reused
  // while guaranteeing a changed one is refetched instead of served stale.
  const path = API_PATHS.drawingPreview(drawingId);
  const res = await fetch(url(version ? `${path}?v=${encodeURIComponent(version)}` : path), {
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new BackendError(res.status, res.statusText, body, res.headers.get("x-request-id"));
  }
  return res.blob();
}

export async function deleteDrawing(
  token: string,
  drawingId: string,
  operationId: string = crypto.randomUUID(),
  collaborationClientId?: string,
): Promise<DrawingDeleteResponse> {
  const res = await fetch(url(API_PATHS.drawing(drawingId)), {
    method: "DELETE",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Idempotency-Key": operationId,
      ...(collaborationClientId ? { "X-Collab-Client-Id": collaborationClientId } : {}),
    },
  });
  return handleResponse(res);
}

export async function abandonDrawingCopy(
  token: string,
  drawingId: string,
): Promise<DrawingCopyAbandonResponse> {
  const res = await fetch(url(API_PATHS.drawingCopyAbandon(drawingId)), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Idempotency-Key": drawingId,
    },
  });
  return handleResponse(res);
}

export async function listOwnedDrawings(
  token: string,
  limit = 20,
  continuationToken?: string,
  deletedDrawingIds: readonly string[] = [],
): Promise<DrawingListResponse> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (continuationToken) query.set("continuationToken", continuationToken);
  deletedDrawingIds.forEach((drawingId) => query.append("deletedDrawingId", drawingId));
  const res = await fetch(url(`${API_PATHS.drawingsMine}?${query}`), {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function listSharedDrawings(
  token: string,
  limit = 20,
  continuationToken?: string,
): Promise<DrawingListResponse> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (continuationToken) query.set("continuationToken", continuationToken);
  const res = await fetch(url(`${API_PATHS.drawingsShared}?${query}`), {
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function createDrawingShare(
  token: string,
  drawingId: string,
  role: DrawingAccessRole,
): Promise<DrawingShareCreateResponse> {
  const res = await fetch(url(API_PATHS.drawingShare(drawingId)), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ role }),
  });
  return handleResponse(res);
}

export async function getDrawingShare(
  token: string,
  drawingId: string,
): Promise<DrawingShareReadResponse> {
  const res = await fetch(url(API_PATHS.drawingShare(drawingId)), {
    method: "GET",
    headers: authHeaders(token),
  });
  return handleResponse(res);
}

export async function redeemDrawingShare(
  token: string,
  shareToken: string,
): Promise<DrawingShareRedeemResponse> {
  const res = await fetch(url(API_PATHS.drawingShareRedeem), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ shareToken }),
  });
  return handleResponse(res);
}

// ---------------------------------------------------------------------------
// AI share grants (design D9, docs/designs/remote-mcp-server.md)
//
// A grant is how a human hands one drawing to an agent without ever seeing a
// UUID: the code IS the reference. Minting is idempotent while active (returns
// the existing code), and revocation is terminal (a revoked code is never
// reissued). These paths are not yet in the shared API_PATHS map, so they are
// built locally here from the shared API_BASE prefix.
// ---------------------------------------------------------------------------

const aiGrantsForDrawingPath = (drawingId: string): string =>
  `${API_BASE}/drawings/${drawingId}/ai-grants`;
const aiGrantsPath = `${API_BASE}/ai-grants`;
const aiGrantPath = (code: string): string => `${API_BASE}/ai-grants/${encodeURIComponent(code)}`;

/**
 * Mint (or re-read) the AI grant for a drawing. Idempotent while active: if an
 * active grant already exists the backend returns that same grant and its
 * original code rather than issuing a second one.
 *
 * The grant carries no access level. The response's `access` reports what the
 * caller can do with the drawing today — which is the ceiling on what an
 * assistant holding the code can do, because the assistant acts as the user.
 * POST /api/drawings/:drawingId/ai-grants
 */
export async function mintAiGrant(token: string, drawingId: string): Promise<MintAiGrantResponse> {
  const res = await fetch(url(aiGrantsForDrawingPath(drawingId)), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: "{}",
  });
  return handleResponse<MintAiGrantResponse>(res);
}

/**
 * List the caller's active AI grants, each paired with the drawing it points at
 * (which may be null when the drawing was deleted or is no longer reachable).
 * GET /api/ai-grants
 */
export async function listAiGrants(
  token: string,
  limit = 24,
  continuationToken?: string,
): Promise<AiGrantListResponse> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (continuationToken) query.set("continuationToken", continuationToken);
  const res = await fetch(url(`${aiGrantsPath}?${query}`), {
    method: "GET",
    headers: authHeaders(token),
  });
  return handleResponse<AiGrantListResponse>(res);
}

export async function lookupAiGrants(
  token: string,
  drawingIds: readonly string[],
): Promise<AiGrantLookupResponse> {
  const res = await fetch(url(`${aiGrantsPath}/lookup`), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ drawingIds }),
  });
  return handleResponse<AiGrantLookupResponse>(res);
}

/**
 * Revoke an AI grant. Terminal: the code is dead forever and re-sharing mints a
 * new, different code. Returns 204 No Content on success.
 * DELETE /api/ai-grants/:code
 */
export async function revokeAiGrant(token: string, code: string): Promise<void> {
  const res = await fetch(url(aiGrantPath(code)), {
    method: "DELETE",
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new BackendError(res.status, res.statusText, body, res.headers.get("x-request-id"));
  }
}

/**
 * Export the current scene to blob-backed shareable link storage.
 * POST /api/sharing/export
 */
export async function exportToLink(token: string, scene: string): Promise<ExportLinkResponse> {
  const body: ExportLinkRequest = { scene };
  const res = await fetch(url(API_PATHS.sharingExport), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return handleResponse<ExportLinkResponse>(res);
}

/**
 * Load a blob-backed shared scene.
 * GET /api/sharing/view/:blobId
 */
export async function loadSharedScene(token: string, blobId: string): Promise<SharedSceneResponse> {
  const res = await fetch(url(API_PATHS.sharingView(blobId)), {
    method: "GET",
    headers: authHeaders(token),
  });
  return handleResponse<SharedSceneResponse>(res);
}

/**
 * Join (or create) a collaboration session.
 * POST /api/session
 */
export async function joinSession(
  token: string,
  drawingId: string,
  driveId: string,
  itemId: string,
  shareToken?: string,
): Promise<SessionJoinResponse> {
  const body: FileSessionRequest = {
    mode: "file",
    drawingId,
    driveId,
    itemId,
    ...(shareToken ? { shareToken } : {}),
  };
  const res = await fetch(url(API_PATHS.session), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  return handleResponse<SessionJoinResponse>(res);
}

/**
 * Search tenant users by name or email for the people picker.
 * GET /api/users/search?q={query}
 */
export async function searchUsers(
  token: string,
  query: string,
  limit = 10,
): Promise<UserSearchResult[]> {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const res = await fetch(url(`${API_PATHS.usersSearch}?${params}`), {
    method: "GET",
    headers: authHeaders(token),
  });
  const data = await handleResponse<{ users: UserSearchResult[] }>(res);
  return data.users ?? [];
}

/**
 * Submit in-app feedback.
 * POST /api/feedback
 */
export async function submitFeedback(
  token: string,
  payload: FeedbackRequest,
): Promise<FeedbackResponse> {
  const res = await fetch(url(API_PATHS.feedback), {
    method: "POST",
    headers: {
      ...(authHeaders(token) as Record<string, string>),
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (res.status === 409) {
    return res.json() as Promise<FeedbackResponse>;
  }
  return handleResponse<FeedbackResponse>(res);
}

/**
 * Health check.
 * GET /api/health
 */
export async function healthCheck(): Promise<{ status: string }> {
  const res = await fetch(url(API_PATHS.health));
  return handleResponse<{ status: string }>(res);
}
